import type { Ref, RefCallback } from 'react';

import warn from './warn';

type Props = Record<string, unknown>;

type AnyRef = Ref<unknown> | undefined | null;

const RESERVED_KEYS = new Set(['key', '__source', '__self']);

const isFn = (v: unknown): v is (...args: unknown[]) => unknown =>
  typeof v === 'function';

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  !!v && typeof v === 'object' && !Array.isArray(v);

const isEventHandlerKey = (k: string) => /^on[A-Z]/.test(k);

const assignRef = (ref: AnyRef, value: unknown) => {
  if (!ref) return;
  if (isFn(ref)) {
    ref(value);
  } else {
    ref.current = value;
  }
};

const createMergedRef = (a: AnyRef, b: AnyRef): RefCallback<unknown> => {
  return (value) => {
    assignRef(a, value);
    assignRef(b, value);
  };
};

const concatClassName = (a: unknown, b: unknown): string | undefined => {
  const parts = [a, b].filter(
    (v): v is string => typeof v === 'string' && v.length > 0,
  );
  return parts.length ? parts.join(' ') : undefined;
};

const toArray = (v: unknown): unknown[] => {
  if (Array.isArray(v)) return v;
  if (v === undefined) return [];
  return [v];
};

const mergeStyle = (a: unknown, b: unknown): unknown => {
  if (Array.isArray(a) || Array.isArray(b)) {
    return [...toArray(a), ...toArray(b)];
  }
  if (isPlainObject(a) && isPlainObject(b)) {
    return { ...a, ...b };
  }
  if (b === undefined) return a;
  return b;
};

const chainEventHandlers = (
  injected: (...args: unknown[]) => unknown,
  consumer: (...args: unknown[]) => unknown,
): ((...args: unknown[]) => unknown) => {
  return (...args) => {
    consumer(...args);
    injected(...args);
  };
};

/**
 * Merge props for `React.cloneElement` calls where the framework injects props
 * into a consumer-supplied child. **Consumer (second argument) wins** on
 * non-mergeable conflicts. Matches the chain order established by
 * `ButtonGroup.tsx` (consumer handler first, injected handler second).
 *
 * Use ONLY for the "framework injects into consumer child" direction. Do not
 * use for default-props / parent-wins patterns.
 *
 * Merge rules:
 * - Event handlers (`on*`, both functions): chain consumer-first, injected-second.
 * - `className`: concatenate (injected + consumer), space-separated.
 * - `style`: array sides concatenate; object sides shallow-merge (consumer
 *   keys win); mixed defers to whichever is defined. Array-concat output is
 *   only valid for Emotion's `css`-style consumers; React's DOM `style` prop
 *   does not accept arrays, so callers injecting `style` onto a DOM child
 *   must not pass array values.
 * - `ref`: callback ref writing through to both refs. Effective only on
 *   React 19, where the consumer child's ref is on `props.ref`. On React
 *   17/18 the consumer ref lives on `element.ref` (outside props) and is
 *   not visible to this utility, so the merge silently no-ops there
 *   (same behavior as before this fix — not a regression).
 * - `key`, `__source`, `__self`: consumer value passed through as-is, never
 *   merged or warned on.
 * - Any other key with both sides defined and not strictly equal: consumer
 *   wins; in non-production, `warn()` once listing the keys.
 *
 * @see DSGN-1645
 */
const mergeProps = <I extends Props, C extends Props>(
  injected: I,
  consumer: C | undefined,
): I & C => {
  if (!consumer) return { ...injected } as I & C;

  const result: Props = {};
  const keys = new Set([...Object.keys(injected), ...Object.keys(consumer)]);
  const conflicts: string[] = [];

  keys.forEach((key) => {
    const i = injected[key];
    const c = consumer[key];

    if (RESERVED_KEYS.has(key)) {
      if (key in consumer) result[key] = c;
      else result[key] = i;
      return;
    }

    if (i === undefined) {
      result[key] = c;
      return;
    }
    if (c === undefined) {
      result[key] = i;
      return;
    }

    if (key === 'ref') {
      result[key] = createMergedRef(i as AnyRef, c as AnyRef);
      return;
    }

    if (isEventHandlerKey(key) && isFn(i) && isFn(c)) {
      result[key] = chainEventHandlers(
        i as (...args: unknown[]) => unknown,
        c as (...args: unknown[]) => unknown,
      );
      return;
    }

    if (key === 'className') {
      result[key] = concatClassName(i, c);
      return;
    }

    if (key === 'style') {
      result[key] = mergeStyle(i, c);
      return;
    }

    if (i === c) {
      result[key] = c;
      return;
    }

    result[key] = c;
    conflicts.push(key);
  });

  if (conflicts.length > 0) {
    warn(
      `mergeProps: consumer values won for keys [${conflicts.join(', ')}]. ` +
        `See @adsk/alloy-react-helpers/mergeProps (DSGN-1645).`,
    );
  }

  return result as I & C;
};

export default mergeProps;
